import { Box, Typography, alpha, useTheme } from "@mui/material";
import { RequestFormContext } from "../RequestFormContext";
import { getCenterStyles, CardDocumentIcon, ClipIcon, FolderUploadIcon, getFocusOutlineSx } from "vig-ui-library";
import { useCallback, useState } from "react";
import { FileTile } from "./FileTile";
import { useDropzone, type FileRejection } from "react-dropzone";
import { uploadLogic } from "../Utilities/UploadHelpers";
import { createRequestFormFileUploader } from "../Utilities/FileUploaderFactory";
import { TaxonomyDto } from "../Schemas/TaxonomyDto";
import { shouldHideCategory } from "../Utilities/mappers";
import {
    buildDropzoneAccept,
    getTaxonomyUploadConstraintsHint,
    getTaxonomyUploadRejectionMessage,
    parseTaxonomyAllowedExtensions,
} from "../Utilities/taxonomyUploadDisplay";
import { areAttachmentUploadDropzonesGloballyVisible } from "../Utilities/attachmentUploadVisibility";
import { getAttachmentFileTaxonomyId } from "../Utilities/fileTaxonomy";

export type AttachmentsProps = Omit<RequestFormContext, "pathToProp"> & {
    /** When true (e.g. in AttachmentsSection), file tiles are omitted — only per-taxonomy drop zones. */
    dropzonesOnly?: boolean;
    /** When true, switch taxonomy grid to one column on medium and smaller screens. */
    singleColumnOnMdDown?: boolean;
}

export const Attachments = (props: AttachmentsProps) => {
    const { dropzonesOnly = false, singleColumnOnMdDown = false, ...contextProps } = props;
    const { isSubmitted, files: filesOrNull, ismanuallySigned, manualSignRequired } = props.request.current;


    const theme = useTheme();

    const files = filesOrNull || [];

    const { previewMode } = props.settings.current;

    if (!areAttachmentUploadDropzonesGloballyVisible(props.request.current, props.settings.current)) return <></>

    const settings = props.settings.current;
    const request = props.request.current;
    const disableUpload = request.isSubmitted || (settings.manualSignRequired ? (request.ismanuallySigned ?? false) : false);
    /** Block only pre-save uploads when taxonomies are conditional; after `requestId` exists, server allows uploads (see `areAttachmentUploadDropzonesGloballyVisible`). */
    const blockUploadDueToDeferredTaxonomy =
        settings.disableImmediateFileUpload && !request.requestId;

    const visibleCategories = (request.categories || []).filter((category) => {
        return !shouldHideCategory(category, request, settings.odvetvieId, props.getRequestFieldsRelationTask.value?.fields ?? []);
    });

    const previewAllowsDropZone =
        !previewMode || (manualSignRequired === true && ismanuallySigned === false);

    const allowChangeAttachmentTaxonomy = !isSubmitted && !previewMode;

    const getTaxonomyUploadState = (taxonomy: TaxonomyDto) => {
        const taxonomyFiles = files.filter((file) => getAttachmentFileTaxonomyId(file) === taxonomy.id);
        const taxonomyFileCount = taxonomyFiles.length;
        const maxNumberOfFiles = taxonomy.maxNumberOfFiles;
        const taxonomyMaxFilesLimit =
            maxNumberOfFiles != null && Number.isFinite(maxNumberOfFiles)
                ? Math.max(0, Math.trunc(maxNumberOfFiles))
                : null;
        const canUploadMoreFilesForTaxonomy =
            taxonomyMaxFilesLimit === null ? true : taxonomyFileCount < taxonomyMaxFilesLimit;
        const showDropZone = canUploadMoreFilesForTaxonomy && previewAllowsDropZone;
        return {
            taxonomyFiles,
            taxonomyFileCount,
            taxonomyMaxFilesLimit,
            canUploadMoreFilesForTaxonomy,
            showDropZone,
        };
    };

    const handleUploadForTaxonomy = (taxonomy: TaxonomyDto, selectedFiles: File[]) => {
        const onlyFiles = selectedFiles.filter((file) => file.type !== "");
        if (!onlyFiles.length) return;
        uploadLogic(onlyFiles, contextProps, createRequestFormFileUploader(contextProps), taxonomy);
    };

    const uncategorizedFiles = files.filter((file) => getAttachmentFileTaxonomyId(file) == null);

    /** When only drop zones are shown, skip taxonomies at max files so the grid does not leave an empty half-width cell; odd last item still spans full width. */
    const gridCategories = dropzonesOnly
        ? visibleCategories.filter((taxonomy) => getTaxonomyUploadState(taxonomy).showDropZone)
        : visibleCategories;


    return <Box sx={{ display: "flex", flexDirection: "column", padding: 3 }}>

        <Box sx={{
            display: "grid",
            gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
            gap: "20px",
            width: "100%",
            ...(singleColumnOnMdDown
                ? {
                    [theme.breakpoints.down("md")]: {
                        gridTemplateColumns: "minmax(0, 1fr)",
                    },
                }
                : {}),
        }}>
            {gridCategories.map((taxonomy, taxonomyIndex) => {
                const {
                    taxonomyFiles,
                    taxonomyFileCount,
                    taxonomyMaxFilesLimit,
                    showDropZone,
                } = getTaxonomyUploadState(taxonomy);

                const spanFullWidth =
                    gridCategories.length % 2 === 1 && taxonomyIndex === gridCategories.length - 1;

                // console.log("Simple upload zone", taxonomy);
                // console.log("getTaxonomyUploadState", getTaxonomyUploadState(taxonomy));

                return (
                    <Box
                        key={taxonomy.id}
                        sx={{
                            gridColumn: spanFullWidth ? "1 / -1" : undefined,
                            display: "flex",
                            flexDirection: "column",
                            gap: 2,
                            width: "100%",
                            alignItems: "stretch",
                        }}
                    >
                        {!dropzonesOnly && (
                            <Box
                                sx={{
                                    display: "flex",
                                    flexWrap: "wrap",
                                    width: "100%",
                                    flexShrink: 0,
                                    gap: 1.5,
                                    alignContent: "flex-start",
                                }}
                            >
                                {taxonomyFiles.map((file) => (
                                    <FileTile
                                        key={`${file.id ?? file.name}-${files.indexOf(file)}`}
                                        {...contextProps}
                                        file={file}
                                        index={files.indexOf(file)}
                                        primaryLabel={file.taxonomy?.name || "Súbor"}
                                        showDeleteButton={!isSubmitted && !!file.uploadCompleted && !previewMode}
                                        showChangeTaxonomyButton={allowChangeAttachmentTaxonomy}
                                        changeTaxonomyTargets={visibleCategories}
                                        clickedStateStrategy="index"
                                    />
                                ))}
                            </Box>
                        )}

                        {showDropZone && (
                            <Box sx={{ flex: 1, width: "100%", display: "flex", minHeight: 0 }}>
                                <TaxonomyUploadZone
                                    taxonomy={taxonomy}
                                    taxonomyMaxFilesLimit={taxonomyMaxFilesLimit}
                                    taxonomyFileCount={taxonomyFileCount}
                                    disabled={disableUpload || blockUploadDueToDeferredTaxonomy}
                                    onFilesSelected={handleUploadForTaxonomy}
                                />
                            </Box>
                        )}
                    </Box>
                );
            })}

            {!dropzonesOnly && uncategorizedFiles.length > 0 && (
                <Box sx={{ padding: 2 }}>
                    <Typography variant="subtitle2" component="p" color="text.secondary" gutterBottom>
                        Nekategorizované súbory
                    </Typography>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
                        {uncategorizedFiles.map((file) => (
                            <FileTile
                                key={`${file.id ?? file.name}-uncategorized-${files.indexOf(file)}`}
                                {...contextProps}
                                file={file}
                                index={files.indexOf(file)}
                                primaryLabel={file.taxonomy?.name || "Súbor nemá kategóriu"}
                                showDeleteButton={!isSubmitted && !!file.uploadCompleted && !previewMode}
                                showChangeTaxonomyButton={allowChangeAttachmentTaxonomy}
                                changeTaxonomyTargets={visibleCategories}
                                clickedStateStrategy="index"
                            />
                        ))}
                    </Box>
                </Box>
            )}
        </Box>
    </Box>
}

export type TaxonomyUploadZoneProps = {
    taxonomy: TaxonomyDto;
    taxonomyMaxFilesLimit: number | null;
    taxonomyFileCount: number;
    disabled: boolean;
    onFilesSelected: (taxonomy: TaxonomyDto, files: File[]) => void;
    /** When the parent already shows the taxonomy name (e.g. section header), hide the title row inside the dashed zone. */
    hideTaxonomyHeading?: boolean;
    /** Compact tile (e.g. collapsible section layout) — small dashed square with folder icon, hint below. */
    variant?: "default" | "section";
};

export const TaxonomyUploadZone = ({
    taxonomy,
    taxonomyMaxFilesLimit,
    taxonomyFileCount,
    disabled,
    onFilesSelected,
    hideTaxonomyHeading = false,
    variant = "default",
}: TaxonomyUploadZoneProps) => {
    const theme = useTheme();
    const [rejectionError, setRejectionError] = useState<string | null>(null);

    const allowedExtList = parseTaxonomyAllowedExtensions(taxonomy.allowedExtensions);
    const accept = buildDropzoneAccept(allowedExtList);
    const maxSize =
        taxonomy.maxFileSize != null && Number.isFinite(taxonomy.maxFileSize) && taxonomy.maxFileSize > 0
            ? taxonomy.maxFileSize
            : undefined;

    const remainingSlots =
        taxonomyMaxFilesLimit === null ? null : Math.max(0, taxonomyMaxFilesLimit - taxonomyFileCount);
    const dropzoneMaxFiles = remainingSlots;
    const allowMultipleInOneDrop =
        dropzoneMaxFiles === null ? true : dropzoneMaxFiles > 1;

    const onDrop = useCallback((acceptedFiles: File[], fileRejections: FileRejection[]) => {
        if (fileRejections.length > 0) {
            setRejectionError(getTaxonomyUploadRejectionMessage(fileRejections, {
                taxonomyName: taxonomy.name,
                allowedExtensions: taxonomy.allowedExtensions,
                maxFileSize: taxonomy.maxFileSize,
                maxNumberOfFiles: taxonomyMaxFilesLimit,
            }));
        } else {
            setRejectionError(null);
        }
        if (acceptedFiles.length > 0) {
            onFilesSelected(taxonomy, acceptedFiles);
        }
    }, [onFilesSelected, taxonomy, taxonomyMaxFilesLimit]);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        disabled,
        multiple: allowMultipleInOneDrop,
        maxFiles: dropzoneMaxFiles ?? undefined,
        accept,
        maxSize,
    });

    const constraintsHint = getTaxonomyUploadConstraintsHint(taxonomy.allowedExtensions, taxonomy.maxFileSize ?? null);

    const sectionBorderIdle = rejectionError
        ? theme.palette.error.main
        : alpha(theme.palette.common.black, 0.2);
    const defaultBorderIdle = rejectionError ? theme.palette.error.main : theme.palette.divider;

    const rejectionErrorText = rejectionError ? (
        <Typography
            variant="body2"
            component="p"
            role="alert"
            sx={{
                fontWeight: 400,
                fontSize: theme.typography.pxToRem(13),
                lineHeight: "132%",
                letterSpacing: 0,
                color: theme.palette.error.main,
                overflowWrap: "anywhere",
            }}
        >
            {rejectionError}
        </Typography>
    ) : null;

    if (variant === "section") {
        return (
            <Box
                sx={{
                    display: "inline-flex",
                    flexDirection: "column",
                    alignItems: "flex-start",
                    flexShrink: 0,
                    width: "auto",
                    maxWidth: "100%",
                    gap: 1,
                }}
            >
                <Box
                    {...getRootProps({
                        role: "button",
                        tabIndex: 0,
                        "aria-label": `Nahrať súbor: ${taxonomy.name}`,
                    })}
                    sx={{
                        display: "inline-flex",
                        flexDirection: "column",
                        alignItems: "flex-start",
                        flexShrink: 0,
                        width: "auto",
                        maxWidth: "100%",

                        border: "1px solid transparent",
                        borderRadius: "8px",
                        ...getFocusOutlineSx(theme, { offset: 0, borderRadius: "8px" }),
                    }}
                >
                    <input
                        {...getInputProps({
                            tabIndex: -1,
                            "aria-hidden": true
                        })}
                    />
                    <Box
                        sx={{
                            ...getCenterStyles("column"),
                            boxSizing: "border-box",
                            width: 102,
                            height: 102,
                            p: "20px",
                            borderRadius: "7px",
                            border: `1.5px dashed ${isDragActive ? theme.palette.primary.main : sectionBorderIdle}`,
                            bgcolor: isDragActive
                                ? alpha(theme.palette.primary.main, 0.06)
                                : rejectionError
                                    ? alpha(theme.palette.error.main, 0.06)
                                    : theme.palette.grey[50],
                            transition: "border-color 0.2s ease, background-color 0.2s ease",
                            cursor: disabled ? "not-allowed" : "pointer",
                            opacity: disabled ? 0.7 : 1,
                            flexShrink: 0,
                            "&:hover": disabled
                                ? {}
                                : {
                                    borderColor: theme.palette.primary.main,
                                },
                        }}
                    >
                        <FolderUploadIcon width={24} height={24} color={theme.palette.grey[600]} />
                    </Box>
                </Box>
                {rejectionErrorText}
            </Box>
        );
    }

    return (
        <Box
            {...getRootProps({
                role: "button",
                tabIndex: 0,
                "aria-label": `Nahrať súbor: ${taxonomy.name}`,
            })}
            sx={{
                border: "2px solid transparent",
                borderRadius: "8px",
                ...getFocusOutlineSx(theme, { offset: 0, borderRadius: "8px" }),
                width: "100%",
                height: "100%",
                flex: 1,
                display: "flex",
                flexDirection: "column",
                minHeight: 0,
                boxSizing: "border-box",
            }}
        >
            <input
                {...getInputProps({
                    tabIndex: -1,
                    "aria-hidden": true
                })}
            />
            <Box sx={{
                ...getCenterStyles("column"),
                width: "100%",
                flex: 1,
                minHeight: 188,
                height: "100%",
                boxSizing: "border-box",
                padding: "20px",
                borderRadius: "7px",
                border: `2px dashed ${isDragActive ? theme.palette.primary.main : defaultBorderIdle}`,
                backgroundColor: isDragActive
                    ? alpha(theme.palette.primary.main, 0.06)
                    : rejectionError
                        ? alpha(theme.palette.error.main, 0.06)
                        : "transparent",
                transition: "border-color 0.2s ease, background-color 0.2s ease",
                cursor: disabled ? "not-allowed" : "pointer",
                opacity: disabled ? 0.7 : 1,
                "&:hover": disabled ? {} : {
                    borderColor: theme.palette.primary.main,
                }
            }}>
                <Box sx={{
                    width: "100%",
                    mb: 0.5,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 0.25,
                    textAlign: "center",
                }}>
                    {!hideTaxonomyHeading && (
                        <Typography
                            variant="subtitle1"
                            component="p"
                            sx={{
                                fontWeight: 700,
                                fontSize: theme.typography.pxToRem(16),
                                lineHeight: theme.typography.pxToRem(24),
                                textAlign: "center",
                                display: "inline",
                                overflowWrap: "anywhere",
                            }}
                        >
                            <Box
                                component="span"
                                sx={{
                                    display: "inline-flex",
                                    verticalAlign: "text-bottom",
                                    marginRight: 0.5,
                                }}
                            >
                                <ClipIcon width={16} height={16} color={theme.palette.primary.main} />
                            </Box>
                            {taxonomy.name}
                        </Typography>
                    )}

                    {constraintsHint ? (
                        <Typography
                            variant="body2"
                            component="p"
                            sx={{
                                fontWeight: 400,
                                fontSize: theme.typography.pxToRem(13),
                                lineHeight: "132%",
                                letterSpacing: 0,
                                textAlign: "center",
                                color: theme.palette.text.secondary,
                            }}
                        >
                            {constraintsHint}
                        </Typography>
                    ) : null}
                </Box>

                <Box sx={{ ...getCenterStyles() }}>
                    <CardDocumentIcon width={48} height={48} color={theme.palette.primary.main} />
                </Box>

                {rejectionErrorText ? (
                    <Box sx={{ width: "100%", mt: 1, textAlign: "center" }}>
                        {rejectionErrorText}
                    </Box>
                ) : null}
            </Box>
        </Box>
    );
};

